import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Users, Flame, Megaphone, Sparkles } from "lucide-react";
import { rm, fmtDate, fmtDateTime } from "@/lib/api";
import { trackClick } from "@/lib/asv";
import { RatingBadge } from "@/components/asv/Reviews";

const IMG = "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=900&q=70";
const IMG_EV = "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=900&q=70";

export const Tag = ({ children, tone = "ink", testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${tone === "accent" ? "asv-accent-bg" : "bg-black/70 text-white backdrop-blur"}`}>{children}</span>
);

const AdTags = ({ x }) => (
  <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
    {x.sponsored && <Tag testid="tag-sponsored"><Megaphone className="h-3 w-3" />Sponsored</Tag>}
    {x.featured && <Tag testid="tag-featured"><Sparkles className="h-3 w-3" />Featured</Tag>}
    {x.bumped && <Tag tone="accent" testid="tag-bumped"><Flame className="h-3 w-3" />Bumped</Tag>}
  </div>
);

const ROW = { card: "flex sm:block", media: "relative min-h-[132px] w-[38%] shrink-0 overflow-hidden sm:w-auto", img: "absolute inset-0", body: "min-w-0 flex-1 p-3 sm:p-4" };
const RATIO = { venue: ["aspect-[4/3]", "sm:aspect-[4/3]"], event: ["aspect-[16/10]", "sm:aspect-[16/10]"] };
const Media = ({ row, kind, src, alt, x }) => (
  <div className={row ? `${ROW.media} ${RATIO[kind][1]}` : `relative overflow-hidden ${RATIO[kind][0]}`} data-testid="card-media">
    <img src={src} alt={alt} className={`${row ? ROW.img : ""} h-full w-full object-cover transition-transform duration-500 group-hover:scale-105`} /><AdTags x={x} />
  </div>
);

export const VenueCard = ({ x, row = false }) => (
  <Link to={`/venues/${x.vendor_slug}/${x.id}`} onClick={() => trackClick(x.campaign_id)} data-testid={`venue-card-${x.id}`} className={`asv-card asv-lift group overflow-hidden ${row ? ROW.card : "block"}`}>
    <Media row={row} kind="venue" src={x.image || IMG} alt={x.name} x={x} />
    <div className={row ? ROW.body : "p-4"}>
      <p className="text-xs font-semibold uppercase tracking-wide asv-accent">{x.sport}</p>
      <p className="asv-display mt-1 line-clamp-1 text-lg font-bold">{x.name}</p>
      <p className="asv-muted line-clamp-1 text-sm">{x.vendor_name}</p>
      <RatingBadge x={x} />
      <div className={`mt-3 flex justify-between text-sm ${row ? "flex-col gap-1 sm:flex-row sm:items-center" : "items-center"}`}>
        <span className="asv-muted flex min-w-0 items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{x.distance_km != null ? `${x.distance_km} km · ` : ""}{x.address || "Malaysia"}</span></span>
        <span className="shrink-0 font-bold">{x.price_from_sen ? `from ${rm(x.price_from_sen)}` : ""}</span>
      </div>
    </div>
  </Link>
);

export const EventCard = ({ x, row = false }) => (
  <Link to={`/events/${x.vendor_slug}/${x.id}`} onClick={() => trackClick(x.campaign_id)} data-testid={`event-card-${x.id}`} className={`asv-card asv-lift group overflow-hidden ${row ? ROW.card : "block"}`}>
    <Media row={row} kind="event" src={x.image || IMG_EV} alt={x.title} x={x} />
    <div className={row ? ROW.body : "p-4"}>
      <p className="flex items-center gap-1 text-xs font-semibold asv-accent"><CalendarDays className="h-3.5 w-3.5" />{fmtDateTime(x.start_at)}</p>
      <p className="asv-display mt-1 line-clamp-2 text-lg font-bold leading-tight">{x.title}</p>
      <p className="asv-muted line-clamp-1 text-sm">{x.vendor_name}</p>
      <div className={`mt-3 flex justify-between text-sm ${row ? "flex-col gap-1 sm:flex-row sm:items-center" : "items-center"}`}>
        <span className="asv-muted">{x.sold_out ? "Sold out" : x.registration_closed ? "Registration closed" : x.remaining != null ? `${x.remaining} seats left` : "Open entry"}</span>
        <span className="font-bold">{x.free ? "Free" : `from ${rm(x.price_from_sen)}`}</span>
      </div>
    </div>
  </Link>
);

export const GameCard = ({ x }) => (
  <Link to={`/games/${x.id}`} data-testid={`game-card-${x.id}`} className="asv-card asv-lift block p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0"><p className="text-xs font-semibold uppercase tracking-wide asv-accent">{x.sport} · {x.skill}</p>
        <p className="asv-display mt-1 line-clamp-2 text-lg font-bold">{x.venue_name}</p></div>
      <span className="asv-soft shrink-0 rounded-full px-3 py-1 text-xs font-bold" data-testid="game-card-count"><Users className="mr-1 inline h-3.5 w-3.5" />{x.joined} / {x.players_needed}</span>
    </div>
    <p className="asv-muted mt-2 text-sm">{fmtDate(x.date)} · {x.time}{x.area ? ` · ${x.area}` : ""}</p>
    <div className="mt-3 flex items-center justify-between text-sm"><span className="asv-muted">Host: {x.host_name}</span>
      <span className="font-bold">{x.spots_left > 0 ? `${x.spots_left} spot${x.spots_left === 1 ? "" : "s"} left` : "Full"}{x.cost_sen ? ` · ${rm(x.cost_sen)}/pax` : ""}</span></div>
  </Link>
);

export const Banner = ({ b }) => b ? (
  <a href={b.link || "#"} target={b.link?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" onClick={() => trackClick(b.campaign_id)} data-testid="asv-banner-ad" className="relative block overflow-hidden rounded-3xl">
    <img src={b.image} alt={b.title} className="h-28 w-full object-cover sm:h-36" />
    <span className="absolute right-3 top-3"><Tag>Sponsored</Tag></span>
  </a>
) : null;

export const Section = ({ title, to, children, testid }) => (
  <section className="mt-14" data-testid={testid}>
    <div className="mb-5 flex items-end justify-between gap-4"><h2 className="asv-display text-2xl font-extrabold sm:text-3xl">{title}</h2>{to && <Link to={to} className="text-sm font-bold asv-accent">See all</Link>}</div>
    {children}
  </section>
);

export const Empty = ({ text }) => <div className="asv-card asv-muted p-10 text-center text-sm" data-testid="asv-empty">{text}</div>;
